import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import './App.css';
import Login from './Components/LoginPage/Login';
import DashboardLayout from './Components/Layout/dashboard/DashboardLayout';
import DashboardRoutes from './Components/Layout/dashboard/DashboardRoutes';
import { store, persistor } from './store/store';
import { logout, setTokens } from './store/userSlice';
import { getDecryptedCookie, removeEncryptedCookie } from './utils/cookieEncryption';
import PaymentStatus from './Components/Layout/MainContent/ExamFees/PaymentStatus';
import CCAvenuePaymentStatus from './Components/Layout/MainContent/ExamFees/CCAvenuePaymentStatus';

// App Content with hooks
const AppContent = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.user);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for auth token in cookies (encrypted)
    const token = getDecryptedCookie('_at');
    const refreshToken = getDecryptedCookie('_rt');

    if (token) {
      // Set tokens in Redux
      dispatch(setTokens({ 
        accessToken: token, 
        refreshToken: refreshToken || null 
      }));
    }

    setIsLoading(false);

    // Listen for logout events
    const handleUnauthorized = () => {
      handleLogout();
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, [dispatch]);

  const handleLogout = () => {
    // Clear only auth tokens from cookies (encrypted)
    removeEncryptedCookie('_at');
    removeEncryptedCookie('_rt');
    
    // Clear Redux state
    dispatch(logout());
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#0d4f5c] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router basename="/StudZone">
      <Routes>
        {/* Public Routes */}
        <Route 
          path="/" 
          element={
            isAuthenticated ? 
            <Navigate to="/home" replace /> : 
            <Login />
          } 
        />
        
        {/* Single payment status route for ALL gateways - like Easebuzz */}
        <Route path="/payment-status" element={<PaymentStatus />} />
        
        {/* Keep these for backward compatibility (they will be redirected by backend) */}
        <Route path="/payment/Success" element={<CCAvenuePaymentStatus />} />
        <Route path="/payment/cancel" element={<CCAvenuePaymentStatus />} />
        
        {/* Protected Routes */}
        <Route
          path="/*"
          element={
            isAuthenticated ? (
              <DashboardLayout onLogout={handleLogout}>
                <DashboardRoutes />
              </DashboardLayout>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        
        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

// Main App
function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={<div>Loading...</div>} persistor={persistor}>
        <AppContent />
      </PersistGate>
    </Provider>
  );
}

export default App;